import { create } from 'zustand';
import { INVENTORY_SIZE, ITEMS, itemOrderRank } from '../data/inventory.config';
import { InventorySaveData, InventorySlot } from '../types/inventory.types';

/**
 * Brings all non-null entries to the front preserving relative order, pads the
 * rest with `null`. The array size stays `INVENTORY_SIZE`.
 */
function compact(slots: (InventorySlot | null)[]): (InventorySlot | null)[] {
  const out: (InventorySlot | null)[] = new Array(INVENTORY_SIZE).fill(null);
  let i = 0;
  for (const s of slots) {
    if (s && s.quantity > 0 && i < INVENTORY_SIZE) {
      out[i++] = s;
    }
  }
  return out;
}

const STORAGE_KEY = 'usagi_inventory';

function buildDefaultSlots(): (InventorySlot | null)[] {
  return new Array<InventorySlot | null>(INVENTORY_SIZE).fill(null);
}

function loadFromStorage(): { slots: (InventorySlot | null)[]; gold: number } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { slots: buildDefaultSlots(), gold: 0 };
    }

    const parsed: InventorySaveData = JSON.parse(raw);
    if (!Array.isArray(parsed.slots)) {
      return { slots: buildDefaultSlots(), gold: 0 };
    }

    // Keep the saved size if it exceeds the default (slots added by Voyageur on
    // the previous run, restored before `generateFloor` recomputed stats).
    const size = Math.max(INVENTORY_SIZE, parsed.slots.length);
    const slots = new Array<InventorySlot | null>(size).fill(null);
    for (let i = 0; i < parsed.slots.length; i++) {
      const s = parsed.slots[i];
      if (
        s &&
        typeof s.itemId === 'string' &&
        typeof s.quantity === 'number' &&
        s.quantity > 0
      ) {
        slots[i] = { itemId: s.itemId, quantity: s.quantity };
      }
    }
    const gold =
      typeof parsed.gold === 'number' && parsed.gold > 0 ? parsed.gold : 0;
    // Compact on read: no interleaved empty slots (the legacy sparse array
    // could leave holes).
    return { slots: compact(slots), gold };
  } catch {
    return { slots: buildDefaultSlots(), gold: 0 };
  }
}

function saveToStorage(slots: (InventorySlot | null)[], gold: number): void {
  try {
    const data: InventorySaveData = { slots, gold };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage may be unavailable
  }
}

interface InventoryState {
  slots: (InventorySlot | null)[];
  /** Carried gold — a separate counter, not an inventory slot. No cap. */
  gold: number;
}

interface InventoryActions {
  /**
   * Add an item to inventory. Returns the quantity that couldn't fit.
   * `'gold'` is intercepted and routed to the `gold` counter (returns 0).
   */
  addItem: (itemId: string, quantity?: number) => number;
  /**
   * Remove quantity of an item. Returns actual amount removed.
   * `'gold'` is intercepted and routed to the `gold` counter.
   */
  removeItem: (itemId: string, quantity?: number) => number;
  /** Add gold to the carried counter (no cap). */
  addGold: (amount: number) => void;
  /** Spend gold. Returns false (no mutation) if the balance is insufficient. */
  spendGold: (amount: number) => boolean;
  setSlot: (index: number, slot: InventorySlot | null) => void;
  clearInventory: () => void;
  /**
   * Rolls dropChance independently for each individual item in each stack.
   * Survivors are kept in their slots (stacks may shrink).
   */
  applyDeathLoss: (dropChance: number) => void;
  /** Sorts by item type (cf. `TYPE_SORT_ORDER`), then re-packs. Idempotent. */
  sortByType: () => void;
  /** Resize inventory to newSize slots. Only grows — never shrinks to avoid losing items. */
  resize: (newSize: number) => void;
  hasRoomFor: (itemId: string, quantity?: number) => boolean;
}

const initial = loadFromStorage();

export const useInventoryStore = create<InventoryState & InventoryActions>(
  (set, get) => ({
    slots: initial.slots,
    gold: initial.gold,

    addItem: (itemId, quantity = 1) => {
      if (itemId === 'gold') {
        get().addGold(quantity);
        return 0;
      }
      const def = ITEMS[itemId];
      if (!def) {
        return quantity;
      }

      let remaining = quantity;
      const slots = [...get().slots];

      for (let i = 0; i < slots.length && remaining > 0; i++) {
        const s = slots[i];
        if (s && s.itemId === itemId && s.quantity < def.maxStack) {
          const space = def.maxStack - s.quantity;
          const add = Math.min(space, remaining);
          slots[i] = { itemId, quantity: s.quantity + add };
          remaining -= add;
        }
      }

      for (let i = 0; i < slots.length && remaining > 0; i++) {
        if (!slots[i]) {
          const add = Math.min(def.maxStack, remaining);
          slots[i] = { itemId, quantity: add };
          remaining -= add;
        }
      }

      const packed = compact(slots);
      saveToStorage(packed, get().gold);
      set({ slots: packed });
      return remaining;
    },

    removeItem: (itemId, quantity = 1) => {
      if (itemId === 'gold') {
        const removed = Math.min(get().gold, quantity);
        get().spendGold(removed);
        return removed;
      }
      let toRemove = quantity;
      const slots = [...get().slots];

      // Iterate from the end so stacks drain from the last slots first.
      for (let i = slots.length - 1; i >= 0 && toRemove > 0; i--) {
        const s = slots[i];
        if (s && s.itemId === itemId) {
          const remove = Math.min(s.quantity, toRemove);
          const newQty = s.quantity - remove;
          slots[i] = newQty > 0 ? { itemId, quantity: newQty } : null;
          toRemove -= remove;
        }
      }

      const packed = compact(slots);
      saveToStorage(packed, get().gold);
      set({ slots: packed });
      return quantity - toRemove;
    },

    addGold: (amount) => {
      if (amount <= 0) {
        return;
      }
      const gold = get().gold + amount;
      saveToStorage(get().slots, gold);
      set({ gold });
    },

    spendGold: (amount) => {
      if (amount <= 0) {
        return true;
      }
      const current = get().gold;
      if (current < amount) {
        return false;
      }
      const gold = current - amount;
      saveToStorage(get().slots, gold);
      set({ gold });
      return true;
    },

    setSlot: (index, slot) => {
      const slots = [...get().slots];
      if (index < 0 || index >= slots.length) {
        return;
      }
      slots[index] = slot;
      const packed = compact(slots);
      saveToStorage(packed, get().gold);
      set({ slots: packed });
    },

    // Clears item slots only — gold is a separate counter and is left
    // untouched (callers move it explicitly when needed).
    clearInventory: () => {
      const slots = buildDefaultSlots();
      saveToStorage(slots, get().gold);
      set({ slots });
    },

    applyDeathLoss: (dropChance) => {
      const slots = get().slots.map((s) => {
        if (!s) {
          return null;
        }
        let kept = 0;
        for (let i = 0; i < s.quantity; i++) {
          if (Math.random() >= dropChance) {
            kept++;
          }
        }
        return kept > 0 ? { itemId: s.itemId, quantity: kept } : null;
      });
      const packed = compact(slots);
      saveToStorage(packed, get().gold);
      set({ slots: packed });
    },

    sortByType: () => {
      const filled = get().slots.filter(
        (s): s is InventorySlot => s !== null && s.quantity > 0,
      );
      // Sort by declaration order in the item catalogue (inventory.config).
      filled.sort((a, b) => itemOrderRank(a.itemId) - itemOrderRank(b.itemId));
      const packed = compact(filled);
      saveToStorage(packed, get().gold);
      set({ slots: packed });
    },

    hasRoomFor: (itemId, quantity = 1) => {
      if (itemId === 'gold') {
        return true;
      }
      const def = ITEMS[itemId];
      if (!def) {
        return false;
      }
      let capacity = 0;
      for (const s of get().slots) {
        if (!s) {
          capacity += def.maxStack;
        } else if (s.itemId === itemId && s.quantity < def.maxStack) {
          capacity += def.maxStack - s.quantity;
        }
        if (capacity >= quantity) {
          return true;
        }
      }
      return capacity >= quantity;
    },

    resize: (newSize) => {
      const current = get().slots;
      if (newSize <= current.length) {
        return;
      }
      const slots = [
        ...current,
        ...new Array<InventorySlot | null>(newSize - current.length).fill(null),
      ];
      saveToStorage(slots, get().gold);
      set({ slots });
    },
  }),
);
